import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Chip,
  Card,
  CardContent,
  IconButton,
  Stack
} from '@mui/material';
import {
  ArrowBack,
  Flight,
  Person,
  Email,
  Phone,
  Cake,
  CheckCircle,
  Cancel,
  Schedule
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../config/api';

const BookingDetails = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get the source of navigation (where did the user come from?)
  const from = location.state?.from; // 'trip' or undefined (default to 'bookings')
  const tripId = location.state?.tripId;

  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/bookings/booking/${bookingId}`);
        setBooking(response.data.booking);
      } catch (err) {
        console.error('Error fetching booking details:', err);
        setError(err.response?.data?.error || 'Failed to load booking details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBookingDetails();
  }, [bookingId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    
    // Handle ISO 8601 duration format (e.g., PT5H49M)
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
    if (match) {
      const hours = match[1] || '0';
      const minutes = match[2] || '0';
      return `${hours}h ${minutes}m`;
    }
    
    // If already formatted, return as is
    return duration;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle />;
      case 'pending': return <Schedule />;
      case 'cancelled': return <Cancel />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  const handleBackNavigation = () => {
    if (from === 'trip' && tripId) {
      navigate(`/trips/${tripId}`);
    } else {
      navigate('/my-bookings');
    }
  };

  if (error || !booking) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Booking not found'}</Alert>
        <Box sx={{ mt: 2 }}>
          <IconButton onClick={handleBackNavigation}>
            <ArrowBack /> Back
          </IconButton>
        </Box>
      </Container>
    );
  }

  const flightDetails = typeof booking.flight_details === 'string'
    ? JSON.parse(booking.flight_details)
    : booking.flight_details;
  const passengers = typeof booking.passengers === 'string'
    ? JSON.parse(booking.passengers)
    : booking.passengers;

  // Extract flight info with fallback for old bookings (pre-enhanced data structure)
  const getFlightInfo = () => {
    if (!flightDetails) return null;
    
    // New format (post-enhancement) has direct fields
    if (flightDetails.origin && flightDetails.destination) {
      return flightDetails;
    }
    
    // Old format - extract from slices array
    const firstSlice = flightDetails.slices?.[0] || flightDetails[0];
    if (!firstSlice) return flightDetails;
    
    return {
      ...flightDetails,
      origin: firstSlice.origin?.iata_code,
      origin_city: firstSlice.origin?.city_name || firstSlice.origin?.name,
      destination: firstSlice.destination?.iata_code,
      destination_city: firstSlice.destination?.city_name || firstSlice.destination?.name,
      departure_time: firstSlice.segments?.[0]?.departing_at,
      arrival_time: firstSlice.segments?.[firstSlice.segments?.length - 1]?.arriving_at,
      duration: firstSlice.duration,
      airline: firstSlice.segments?.[0]?.operating_carrier?.name || firstSlice.segments?.[0]?.marketing_carrier?.name,
      airline_code: firstSlice.segments?.[0]?.operating_carrier?.iata_code || firstSlice.segments?.[0]?.marketing_carrier?.iata_code,
      flight_number: firstSlice.segments?.[0]?.operating_carrier_flight_number || firstSlice.segments?.[0]?.marketing_carrier_flight_number,
      stops: (firstSlice.segments?.length || 1) - 1,
      booking_reference: flightDetails.booking_reference,
      // Return flight (if exists)
      return_departure_time: flightDetails.slices?.[1]?.segments?.[0]?.departing_at,
      return_arrival_time: flightDetails.slices?.[1]?.segments?.[flightDetails.slices[1]?.segments?.length - 1]?.arriving_at,
      return_duration: flightDetails.slices?.[1]?.duration
    };
  };
  
  const enhancedFlightDetails = getFlightInfo();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        {/* Header with Back Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={handleBackNavigation}
            sx={{ mr: 2, color: 'primary.main' }}
          >
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Booking Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Booking ID: {booking.id}
            </Typography>
          </Box>
          <Chip
            icon={getStatusIcon(booking.status)}
            label={booking.status || 'confirmed'}
            color={getStatusColor(booking.status)}
            sx={{ textTransform: 'capitalize' }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Booking Reference */}
        <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Booking Reference
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
            {booking.booking_reference || enhancedFlightDetails?.booking_reference || booking.duffel_order_id}
          </Typography>
        </Box>

        {/* Flight Information */}
        <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              <Flight sx={{ mr: 1, verticalAlign: 'middle' }} />
              Flight Details
            </Typography>

            <Grid container spacing={3}>
              {/* Origin */}
              <Grid item xs={12} md={5}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {enhancedFlightDetails?.origin || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {enhancedFlightDetails?.origin_city || 'Departure'}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {formatDate(enhancedFlightDetails?.departure_time || booking.created_at)}
                  </Typography>
                </Box>
              </Grid>

              {/* Arrow */}
              <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Flight sx={{ fontSize: 40, color: 'primary.main', transform: 'rotate(90deg)' }} />
              </Grid>

              {/* Destination */}
              <Grid item xs={12} md={5}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {enhancedFlightDetails?.destination || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {enhancedFlightDetails?.destination_city || 'Arrival'}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {formatDate(enhancedFlightDetails?.arrival_time)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Additional Flight Info */}
            {(enhancedFlightDetails?.airline || enhancedFlightDetails?.flight_number || enhancedFlightDetails?.duration) && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  {enhancedFlightDetails?.airline && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Airline
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {enhancedFlightDetails.airline}
                      </Typography>
                    </Grid>
                  )}
                  {enhancedFlightDetails?.flight_number && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Flight Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {enhancedFlightDetails.airline_code || ''}{enhancedFlightDetails.flight_number}
                      </Typography>
                    </Grid>
                  )}
                  {enhancedFlightDetails?.duration && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Duration
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDuration(enhancedFlightDetails.duration)}
                      </Typography>
                    </Grid>
                  )}
                  {enhancedFlightDetails?.stops !== undefined && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Stops
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {enhancedFlightDetails.stops === 0 ? 'Non-stop' : `${enhancedFlightDetails.stops} stop${enhancedFlightDetails.stops > 1 ? 's' : ''}`}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Passengers */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
              Passenger{passengers?.length !== 1 ? 's' : ''} ({passengers?.length || 0})
            </Typography>

            {passengers && passengers.map((passenger, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  mb: index < passengers.length - 1 ? 2 : 0,
                  bgcolor: '#f9f9f9',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0'
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Passenger {index + 1}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Person fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Name
                        </Typography>
                        <Typography variant="body2">
                          {passenger.title} {passenger.firstName || passenger.given_name} {passenger.lastName || passenger.family_name}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  {passenger.email && (
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Email fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body2">
                            {passenger.email}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}
                  {passenger.phone && (
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Phone fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body2">
                            {passenger.phone || passenger.phoneNumber || passenger.phone_number}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}
                  {passenger.dateOfBirth && (
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Cake fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Date of Birth
                          </Typography>
                          <Typography variant="body2">
                            {new Date(passenger.dateOfBirth || passenger.born_on).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* Pricing */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Payment Summary
            </Typography>

            <Box>
              {booking.total_amount && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Flight Cost
                  </Typography>
                  <Typography variant="body2">
                    {booking.currency || 'USD'} {parseFloat(booking.total_amount).toFixed(2)}
                  </Typography>
                </Box>
              )}
              {booking.profit_amount && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Service Fee
                  </Typography>
                  <Typography variant="body2">
                    {booking.currency || 'USD'} {parseFloat(booking.profit_amount).toFixed(2)}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Total Paid
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {booking.customer_paid_currency || booking.currency || 'USD'} {parseFloat(booking.customer_paid_amount || booking.total_amount || 0).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Booking Info */}
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Booked on: {formatDate(booking.created_at)}
          </Typography>
          {booking.duffel_order_id && (
            <Typography variant="caption" color="text.secondary" display="block">
              Duffel Order ID: {booking.duffel_order_id}
            </Typography>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default BookingDetails;

